import brand from '@helpers/brand';
import { cssVar, setCssVar } from '@helpers/cssVar';
import fonts from '@helpers/fonts';
import { lineClamp } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import { ReactComponent as SvgChevron } from '@img/icons/chevron-right.svg';
import { ReactComponent as SvgExternal } from '@img/icons/external-link.svg';
import { m } from 'framer-motion';
import styled, { css } from 'styled-components';
import { NavigationDropdownMode } from '../../Navigation.types';
const Container = styled(m.div) `
  ${setCssVar('accentColor', brand.hta.greenLight)};
  ${setCssVar('accentColorAlt', brand.grey)};
  ${setCssVar('bgColor', brand.white)};
  ${setCssVar('fgColor', brand.black)};
  ${setCssVar('fgColorAlt', brand.white)};

  .theme-apl & {
    ${setCssVar('accentColor', brand.apl.greenLight)};
    ${setCssVar('accentColorAlt', brand.grey)};
    ${setCssVar('bgColor', brand.white)};
    ${setCssVar('fgColor', brand.black)};
    ${setCssVar('fgColorAlt', brand.white)};
  }

  .theme-hh & {
    ${setCssVar('accentColor', brand.hortHouse.greenLight)};
    ${setCssVar('accentColorAlt', brand.white)};
    ${setCssVar('bgColor', brand.grey)};
    ${setCssVar('fgColor', brand.black)};
    ${setCssVar('fgColorAlt', brand.white)};
  }

  position: absolute;
  top: 100%;
  left: 0;
  display: block;
  background: ${cssVar('bgColor')};
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  z-index: 10000;

  ${({ mode }) => mode === NavigationDropdownMode.OneLevel &&
    css `
      ${Column1} {
        grid-column: 1 / span 12;
      }

      @media ${from(Device.TabletLarge)} {
        ${Column1} {
          grid-column: 1 / span 12;
        }

        ${List} {
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: 30px;
        }

        ${Item} {
          grid-column: span 4;
        }

        ${Link} {
          height: 100px;
        }
      }

      @media ${from(Device.Desktop)} {
        ${Item} {
          grid-column: span 3;
        }

        ${Link} {
          height: 125px;
        }
      }
    `}

  ${({ mode }) => mode === NavigationDropdownMode.OneLevelPromo &&
    css `
      ${Column1} {
        grid-column: 1 / span 12;
      }

      @media ${from(Device.TabletLarge)} {
        ${Column1} {
          grid-column: 1 / span 12;
        }

        ${List} {
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: 30px;
        }

        ${Item} {
          grid-column: span 4;
        }

        ${Link} {
          height: 100px;
          font-size: 18px;
          line-height: 28px;
        }
      }

      @media ${from(Device.Desktop)} {
        ${Item} {
          grid-column: span 6;
        }
        ${Column1} {
          grid-column: 1 / span 6;
        }

        ${Column3} {
          display: block;
          grid-column: 8 / span 4;
          margin-left: 0;
        }

        ${Link} {
          height: 125px;
          font-size: 22px;
          line-height: 32px;
        }
      }
    `}

    ${({ mode }) => mode === NavigationDropdownMode.OneLevelImage &&
    css `
      ${Column1} {
        grid-column: 1 / span 12;
      }

      @media ${from(Device.TabletLarge)} {
        ${Column1} {
          grid-column: 1 / span 12;
        }

        ${List} {
          display: flex;
          grid-template-columns: repeat(12, 1fr);
          gap: 30px;
        }

        ${Item} {
          grid-column: span 2;
        }
      }
    `}

  ${({ mode }) => mode === NavigationDropdownMode.TwoLevels &&
    css `
      ${Column1} {
        grid-column: 1 / span 3;

        ${Chevron} {
          right: 20px;
          height: 21px;
          width: 10px;
        }
      }

      ${Column2} {
        grid-column: 5 / span 7;
      }

      @media ${from(Device.TabletLarge)} {
        ${Column1} {
          grid-column: 1 / span 4;

          ${List} {
            display: grid;
            grid-template-columns: 1fr;
            gap: 6px;
          }

          ${Link} {
            padding-right: 70px;
            position: relative;
          }
        }
        ${Column2} {
          position: relative;
          grid-column: 6 / span 7;

          &:before {
            content: '';
            display: block;
            position: absolute;
            top: 20px;
            left: -9%;
            height: calc(100% - 40px);
            width: 1px;
            background-color: ${brand.greyVeryLight};

            @media ${from(Device.Desktop)} {
              left: -10%;
            }
          }

          ${List} {
            display: flex;
            gap: 20px;

            @media ${from(Device.Desktop)} {
              gap: 30px;
            }
          }

          ${Item} {
            flex: 0 0 190px;

            @media ${from(Device.Desktop)} {
              flex: 0 0 248px;
            }
          }
        }
      }

      @media ${from(Device.Desktop)} {
        ${Column1} {
          grid-column: 1 / span 3;
        }

        ${Column2} {
          grid-column: 5 / span 7;
        }
      }
    `}

  ${({ mode }) => mode === NavigationDropdownMode.TwoLevelsPromo &&
    css `
      ${Column1} {
        grid-column: 1 / span 4;
      }

      ${Column2} {
        grid-column: 5 / span 4;
      }

      ${Column3} {
        display: none;
      }

      @media ${from(Device.TabletLarge)} {
        ${Inner} {
          grid-template-columns: repeat(24, 1fr);
        }

        ${List} {
          display: grid;
          flex-direction: column;
        }

        ${Link} {
          padding: 8px 30px 8px 15px;
          border-radius: 0;
          font-size: 18px;
          line-height: 28px;
          background-color: transparent;

          .theme-hh & {
            background-color: transparent;
            color: ${cssVar('fgColorAlt')};

            &:hover {
              color: ${cssVar('fgColor')};
              background-color: ${cssVar('accentColor')};
            }
          }
        }

        ${Column1} {
          grid-column: 1 / span 10;
        }

        ${Column2} {
          grid-column: 11 / span 14;

          ${Link} {
            ${fonts.sourceSansPro.regular};
          }
        }
      }

      @media ${from(Device.Desktop)} {
        ${Column1} {
          grid-column: 1 / span 7;
        }

        ${Column2} {
          grid-column: 9 / span 6;
        }

        ${Column3} {
          display: block;
          grid-column: 15 / span 8;
          margin-left: 0;
        }
      }
    `}
`;
const DropDownTop = styled.div `
  ${fonts.sourceSansPro.semiBold};
  padding: 12px 0 6px;
  border-bottom: 1px solid ${brand.greyVeryLight};
`;
const Inner = styled.div `
  position: relative;
  display: flex;
  flex-direction: row;
  height: 100%;
  margin: 0 auto;
  padding: 0;
  width: 100%;
  display: grid;
  gap: 15px;
  grid-template-columns: repeat(12, 1fr);

  @media ${until(Device.TabletLarge)} {
    display: flex;
    gap: 0; // resetting as only chrome supports this with flex.
    max-width: none;
    padding: 0 15px;
  }

  @media ${from(Device.Desktop)} {
    gap: 30px;
  }
`;
const Column = styled(m.div) `
  display: block;
  flex: 0 1 auto;
  grid-row: 1;
  transition: opacity 0.15s ease-out;

  /* ===== Scrollbar CSS ===== */
  /* Firefox */
  & {
    scrollbar-width: auto;
    scrollbar-color: #669900 #e5e5e5;
  }

  /* Chrome, Edge, and Safari */
  &::-webkit-scrollbar {
    width: 12px;
    margin-right: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #e5e5e5;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #669900;
    border-radius: 10px;
    border: 3px solid #e5e5e5;
  }
`;
const Column1 = styled(Column) `
  @supports (display: grid) {
  }
`;
const Column2 = styled(Column) `
  display: block;

  &::-webkit-scrollbar-track {
    background: #ffffff;
  }

  &::-webkit-scrollbar-thumb {
    border-color: #ffffff;
  }
`;
const Column3 = styled(Column) `
  display: none;

  ${({ isActive }) => isActive &&
    css `
      display: block;
    `}
`;
const List = styled.ul `
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  margin: 0;
  padding: 4px 0;
`;
const Item = styled.li `
  display: block;
  list-style: none;
  margin: 0;
  padding: 0;
`;
const ContentHolder = styled.div `
  ${fonts.sourceSansPro.semiBold};
`;
const Link = styled.a `
  position: relative;
  display: block;
  color: ${cssVar('fgColor')};
  font-size: 22px;
  line-height: 32px;
  margin: 0;
  padding: 13px 15px 12px;
  text-decoration: none;
  transition: background-color 0.15s ease-out, background-size 0.15s ease-out, color 0.15s ease-out;
  border-radius: 7px;
  overflow: hidden;

  @media ${from(Device.TabletLarge)} {
    font-size: 18px;
    line-height: 28px;
    padding: 10px 15px 9px;
  }

  @media ${from(Device.DesktopMedium)} {
    font-size: 22px;
    line-height: 32px;
  }

  ${Column2} & {
    font-weight: 400;
  }

  ${Column3} & {
    padding-left: 50px;
  }

  .theme-hh & {
    background-color: ${cssVar('fgColorAlt')};
  }

  &:focus,
  &:hover {
    text-decoration: none;
    background-color: ${cssVar('accentColor')};

    ${({ hasImage }) => hasImage &&
    css `
        ${ContentHolder} {
          background-color: ${cssVar('accentColor')};
        }
      `}

    .theme-hh & {
      p {
        color: ${cssVar('fgColor')};
      }
    }
  }

  ${({ hasImage }) => hasImage &&
    css `
      padding: 0;
      color: ${cssVar('fgColor')};
      text-decoration: none;
      font-size: 18px;
      line-height: 26px;
      height: 223px;
      display: flex;
      flex-direction: column;

      @media ${from(Device.TabletLarge)} {
        padding: 0;
      }

      @media ${from(Device.Desktop)} {
        padding: 0;
        height: 263px;
      }

      ${ContentHolder} {
        transition: background-color 0.15s ease-out;
        border: 1px solid ${brand.greyVeryLight};
        background-color: ${cssVar('fgColorAlt')};
        color: ${cssVar('fgColor')};
        border-top: none;
        border-radius: 0 0 7px 7px;
        padding: 10px 8px;
        flex: 1 0 auto;
      }
    `}

  ${({ isActive }) => isActive &&
    css `
      background-color: ${cssVar('accentColor')};
      text-decoration: none;
    `}

    ${({ isExternal }) => isExternal &&
    css `
      padding-right: 40px;

      @media ${from(Device.TabletLarge)} {
        padding-right: 40px;
      }

      @media ${from(Device.Desktop)} {
        padding-right: 40px;
      }
    `}
`;
const AreaLink = styled.a `
  position: relative;
  display: inline-block;
  color: ${cssVar('fgColor')};
  font-size: 18px;
  line-height: 24px;
  margin: 0;
  padding: 10px 15px 9px;
  text-decoration: none;
  transition: background-color 0.15s ease-out, background-size 0.15s ease-out, color 0.15s ease-out;
  overflow: hidden;

  @media ${from(Device.TabletLarge)} {
    font-size: 20px;
    line-height: 24px;
    padding: 8px 12px 7px;
  }

  @media ${from(Device.DesktopMedium)} {
    font-size: 22px;
    line-height: 32px;
    padding: 10px 15px 9px;
  }

  &:hover,
  &:focus {
    background-color: ${cssVar('accentColor')};

    .theme-hh & {
      color: ${cssVar('accentColorAlt')};
    }
  }

  .theme-hh & {
    color: ${cssVar('fgColorAlt')};
  }
`;
const LinkDescription = styled.p `
  ${lineClamp(2, 'block')};
  ${fonts.sourceSansPro.regular};
  font-size: 16px;
  line-height: 22px;
  margin-top: 6px;
  color: ${cssVar('accentColorAlt')};
  width: 100%;

  @media ${from(Device.Desktop)} {
    font-size: 18px;
    line-height: 25px;
    margin-top: 8px;
  }
`;
const BgImageHolder = styled.div `
  height: 140px;
  width: 190px;
  position: relative;
  overflow: hidden;

  @media ${from(Device.Desktop)} {
    height: 190px;
    width: 248px;
  }
`;
const PromoImageWrapper = styled.div `
  margin: 20px 0;
  height: 337px;
  width: 100%;
  max-width: 528px;
  position: relative;
  overflow: hidden;
  border-radius: 24px;
`;
const BgImage = styled.img `
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  transform: scale(1);
  transition: transform ease-out 1s;
  width: 100%;

  ${Link}:focus &,
  ${Link}:hover & {
    transform: scale(1.1);
  }
`;
const Chevron = styled(SvgChevron) `
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  display: block;
  height: 14px;
  width: 7px;
`;
const External = styled(SvgExternal) `
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  display: block;
  height: 17px;
  width: 17px;
`;
export default {
    BgImage,
    BgImageHolder,
    Chevron,
    Column1,
    Column2,
    Column3,
    Container,
    ContentHolder,
    DropDownTop,
    External,
    Inner,
    Item,
    Link,
    AreaLink,
    LinkDescription,
    List,
    PromoImageWrapper,
};
